import { useMemo } from "react";
import { useSelector } from "react-redux";
import { useBiCommonDataContext } from "../../../../../contexts/BiCommonDataContext.ts";
import { selectListSettingsState } from "../../../../../store/selectors/fieldsStateSelectors.ts";
import { getListAggregationModel, getListColumns } from "../../utils/listGridHelper.ts";
import useListReportConfiguration from "./useListReportConfiguration.ts";
import useListRowsFetcher from "./useListRowsFetcher.ts";
import useListSorting from "./useListSorting.ts";
import useListReportDataSource from "../../hooks/useListReportDataSource.ts";

const useListPreviewGrid = () => {
  const { onRefreshGrid } = useBiCommonDataContext();
  const settings = useSelector(selectListSettingsState);
  const { activeConfiguration, validity, buildProgress, exportRequestConfig } = useListReportConfiguration();
  const { sortModel, onSortingChanged } = useListSorting();
  const fetchRows = useListRowsFetcher(activeConfiguration.fields);
  const configuration = useMemo(
    () => ({ ...activeConfiguration, settings: activeConfiguration.payload.settings }),
    [activeConfiguration]
  );
  const result = useListReportDataSource(configuration, sortModel, fetchRows, !activeConfiguration.validity.valid);
  const displayedConfiguration = result.configuration;
  const showGrandTotal = displayedConfiguration.settings.showGrandTotal === true;
  const columns = useMemo(
    () => getListColumns(displayedConfiguration.fields, showGrandTotal, result.failedMeasures),
    [displayedConfiguration.fields, showGrandTotal, result.failedMeasures]
  );
  const aggregationModel = useMemo(
    () => getListAggregationModel(displayedConfiguration.fields, showGrandTotal),
    [displayedConfiguration.fields, showGrandTotal]
  );
  const pinnedColumns = useMemo(() => ({ left: settings.freezedColumns }), [settings.freezedColumns]);

  return {
    validity,
    resultId: result.resultId,
    error: result.error,
    failedMeasures: result.failedMeasures,
    dataSource: result.dataSource,
    totalRows: result.totalRows,
    loading: buildProgress !== undefined || result.isLoading,
    columns,
    sortModel: result.sortModel,
    aggregationModel,
    pinnedColumns,
    onRefresh: onRefreshGrid,
    onSortingChanged,
    exportRequestConfig,
  };
};

export default useListPreviewGrid;
